"use client";

import { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
} from "@/ui";
import toast from "react-hot-toast";
import { useAxios } from "@/app/providers/AxiosProvider";
import useUnifiedCandidateSearch from "@lib/integrations/unified/useUnifiedCandidateSearch";
import { useJobStatus } from "@lib/jobs/useJobStatus";
import { JobStatus } from "@lib/enums";

export default function ImportFromATS({ open, onClose, onImportStarted }) {
  const api = useAxios();
  const { addJob, updateJob, removeJobs } = useJobStatus();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Reset the search/selection when the dialog closes (React's replacement for
  // a set-state-in-effect).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
      setSelected(new Set());
    }
  }

  const { candidates, isLoading } = useUnifiedCandidateSearch(
    debouncedQuery,
    open
  );

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleImport = async () => {
    const ids = Array.from(selected);
    onClose();

    // Immediately surface a loading state in the top StatusBar (mirrors
    // CVUpload) using a temporary, attempt-unique job id per candidate.
    const stamp = Date.now();
    const pending = ids.map((id) => {
      const tempJobId = `unified-${id}-${stamp}`;
      const name = candidates.find((c) => c.id === id)?.name ?? id;
      addJob(tempJobId, { status: JobStatus.QUEUED, metadata: { name } });
      return { id, tempJobId, name };
    });

    for (const { id, tempJobId, name } of pending) {
      try {
        const { data } = await api.post(
          `/integrations/unified/candidates/${encodeURIComponent(id)}/import`
        );

        // Re-key the placeholder to the server's fileId so it reconciles with
        // the processing job streamed back from the API.
        if (data?.file_id) {
          updateJob(tempJobId, { id: data.file_id });
        } else {
          removeJobs([tempJobId]);
        }
      } catch (err) {
        removeJobs([tempJobId]);
        toast.error(`Failed to import ${name}`);
      }
    }

    onImportStarted?.();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Import from ATS</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogBody>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Search by name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box sx={{ minHeight: 280, maxHeight: 360, overflow: "auto" }}>
              {debouncedQuery.length < 2 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  Type at least 2 characters to search.
                </Typography>
              ) : isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                    py: 4,
                  }}
                >
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Searching your ATS…
                  </Typography>
                </Box>
              ) : candidates.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No candidates found.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {candidates.map((c) => {
                    const disabled = c.has_resume === false;
                    const checked = selected.has(c.id);
                    const secondary = [c.company, c.emails?.[0]]
                      .filter(Boolean)
                      .join(" · ");

                    const row = (
                      <ListItem key={c.id} disablePadding>
                        <ListItemButton
                          onClick={() => !disabled && toggle(c.id)}
                          disabled={disabled}
                          dense
                        >
                          <Checkbox
                            edge="start"
                            checked={checked}
                            disabled={disabled}
                            tabIndex={-1}
                            disableRipple
                          />
                          <ListItemText
                            primary={c.name || "(Unnamed)"}
                            secondary={
                              <>
                                {c.title && (
                                  <Typography component="span" variant="body2" color="text.primary">
                                    {c.title}
                                  </Typography>
                                )}
                                {c.title && secondary && " — "}
                                {secondary}
                              </>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );

                    return disabled ? (
                      <Tooltip key={c.id} title="No CV on record" placement="left">
                        <span>{row}</span>
                      </Tooltip>
                    ) : (
                      row
                    );
                  })}
                </List>
              )}
            </Box>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={selected.size === 0}
            >
              Import {selected.size > 0 ? `${selected.size} ` : ""}candidate
              {selected.size === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
